import React, { useCallback } from "react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import { useSelector, useDispatch } from "react-redux";
import { EditOutlined, DeleteOutlined, MoreVert } from "@mui/icons-material";
import { IconButton } from "@mui/material";

import { toggleCheckbox } from "../../../../redux/CheckboxSclice";
import { deleteWorklog } from "../../../../sevices/apiWorklog";
import CustomDateFormatter from "../dateTimeFormateConverter/dateTimeFormateConverter";
import EditWorklog from "../../components/worklog/editWorklog/editWorklog";
import { compareDates } from "../../../../utils";
import WorklogText from "./worklogTextEditor";
import warningIcon from "../../../../styles/svg/warningIcon.svg";
import { useResponsive } from "../../../../hooks/useResponsive";
import "./worklog.scss";
import useMutate from "../../../../hooks/useMutate";
import WorklogAvatar from "./WorklogAvatar";
import WorklogDialog from "./WorklogDialog";
import WorklogActions from "./WorklogActions";
import WorklogDetails from "./WorklogDetails";
import CustomMenu from "../../../../components/Menu";
import CustomMenuItem from "../../../../components/Menu/CustomMenuItem";

function ListWorklog({
  data,
  moveWorklogPermission,
  cardId,
  showIcon = false,
  boardPermission, // This will be passed from the parent component
}) {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const isMobile = useResponsive("down", "md");
  const isBetween300And699 = useResponsive("between", 300, 699);
  const checked = useSelector((state) =>
    state.checkbox.checkedIds.includes(data?.card_log_id)
  );
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);

  const { mutate: deleteWorklogMutate } = useMutate(deleteWorklog,[],
    ()=>{},
    () => {
      queryClient.invalidateQueries({
        queryKey: [`worklog`, cardId],
      });
      queryClient.invalidateQueries({
        queryKey: [`history`, String(cardId)],
      });
      queryClient.invalidateQueries({
        queryKey: [`card`, cardId],
      });
      setLoading(false);
      handleClose();
    },
    () => {
      setLoading(false);
      handleClose();
    },
  );

  const handleChange = useCallback((id) => {
    dispatch(toggleCheckbox(id));
  }, [dispatch]);

  const handleEdit = useCallback(() => {
    setShowEdit(true);
  }, []);

  const handleDelete = useCallback(() => {
    setShowDelete(true);
  }, []);

  const handleClose = useCallback(() => {
    setShowDelete(false);
  }, []);

  const callback = useCallback(() => {
    setLoading(true);
    deleteWorklogMutate(data?.card_log_id);
  }, [data?.card_log_id, deleteWorklogMutate]);

  return (
    <>
      <WorklogDialog
        showDelete={showDelete}
        handleClose={handleClose}
        loading={loading}
        callback={callback}
      />
      {showEdit && (
        <EditWorklog
          card_log_id={data?.card_log_id}
          cardId={cardId}
          showEdit={showEdit}
          setShowEdit={setShowEdit}
        />
      )}
      <Box
        sx={{
          flexGrow: 1,
          px: isMobile && "",
          pl: isMobile ? 1 : "",
          width: isMobile ? "96%" : "100%",
        }}
      >
        <Grid container spacing={2} className="worklog-container">
          <div
            className="worklog-box"
            style={{ width: isMobile ? "100%" : "" }}
          >
            <WorklogAvatar data={data} showIcon={showIcon} />
            <div
              className="worklog-box-body"
              style={{ width: isMobile ? "100%" : "" }}
            >
              <div className="d-flex justify-content-space-between">
                <Grid className="worklog-detail w-100">
                  <Grid className="d-flex justify-content-space-between w-100">
                    <WorklogDetails data={data} isMobile={isMobile} />
                    {isMobile && <div>
                      <IconButton
                        onClick={(e) => setAnchorEl(e.currentTarget)}
                        className="worklog-menu-button"
                      >
                        <MoreVert />
                      </IconButton>
                      <CustomMenu
                        anchorEl={anchorEl}
                        open={anchorEl}
                        handleClose={() => setAnchorEl(null)}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "right",
                        }}
                        anchorOrigin={{
                          horizontal: "right",
                          vertical: "bottom",
                        }}
                        className="worklog-menu-items"
                      >
                        <CustomMenuItem
                          onClick={handleEdit}
                          className="worklog-menu-items-item"
                        >
                          <EditOutlined className="icon" />
                          Edit
                        </CustomMenuItem>
                        <CustomMenuItem
                          onClick={handleDelete}
                          className="worklog-menu-items-item"
                        >
                          <DeleteOutlined className="icon" />
                          Delete
                        </CustomMenuItem>
                      </CustomMenu>
                    </div>}
                  </Grid>
                  {moveWorklogPermission && (
                    <Checkbox
                      checked={checked}
                      onChange={(e) => handleChange(data?.card_log_id)}
                      inputProps={{ "aria-label": "controlled" }}
                      className="pa-0 ml-1 checkbox-height"
                    />
                  )}
                </Grid>
                <Grid>
                  {!isBetween300And699 && (
                    <Typography className="worklog-box-date ">
                      <div className="d-flex align-items-center justify-content-end">
                        {!compareDates(
                          data?.created_at,
                          data?.worklog_time
                        ) && (
                          <div className="warningicon">
                            {" "}
                            <img src={warningIcon} alt="" />{" "}
                          </div>
                        )}
                        <div
                          className={`${
                            isMobile
                              ? "created-date-worklog"
                              : "worklog-box-date-field"
                          } ml-1`}
                        >
                          <CustomDateFormatter date={data?.worklog_time} />
                        </div>
                      </div>
                      {!isMobile && (
                        <div className="created-date-worklog">
                          Created at{" "}
                          {<CustomDateFormatter date={data?.modified_at} />}
                        </div>
                      )}
                    </Typography>
                  )}
                </Grid>
              </div>
              <Grid>
                <WorklogText text={data?.work_performed} />
              </Grid>
              {!isMobile && (
                <WorklogActions
                  data={data}
                  handleEdit={handleEdit}
                  handleDelete={handleDelete}
                  boardPermission={boardPermission}
                />
              )}

              {isBetween300And699 && (
                <Typography className="worklog-box-date ">
                  <div className="d-flex align-items-center">
                    {!compareDates(data?.created_at, data?.worklog_time) && (
                      <div className="warningicon">
                        {" "}
                        <img src={warningIcon} alt="" />{" "}
                      </div>
                    )}
                    <div
                      className={`${
                        isMobile
                          ? "created-date-worklog"
                          : "worklog-box-date-field"
                      } `}
                    >
                      <CustomDateFormatter date={data?.worklog_time} />
                    </div>
                  </div>
                  {!isMobile && (
                    <div className="created-date-worklog">
                      Created at{" "}
                      {<CustomDateFormatter date={data?.modified_at} />}
                    </div>
                  )}
                </Typography>
              )}
            </div>
          </div>
        </Grid>
      </Box>
    </>
  );
}

export default React.memo(ListWorklog);
